import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { ILoginState, IMenuState, IMyProfileState } from '@share/store/slices';
import { RootState, getPreferenceFileUrlFromAccount } from '@share/utils';
import { IAccount, LOGO_FIELD_KEY } from '@share/common-types';
import { Routes } from '@share/constants';
import { isEmpty } from 'lodash';
import { ModernSidebar } from '../../home/home-modern/modern-sidebar';

import './style.scss';

interface IMapStateToProps {
  loginStore: ILoginState;
  menuStore: IMenuState;
  myProfileStore: IMyProfileState;
}

interface IProps extends IMapStateToProps {
  children: React.ReactNode;
}

function ModernSidebarGlobalComponent(props: IProps) {
  const { loginStore, menuStore, myProfileStore, children } = props;
  const { account, user } = loginStore;
  const { items } = menuStore;
  const { result } = myProfileStore;
  const profile = result?.profile;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if Modern template should be used (both SiteId AND OrgId must match)
  const siteId = items?.siteId;
  const organizationId = items?.organizationId;
  const modernHomeTemplateOrgidList = account?.modernHomeTemplateOrgidList;
  const modernHomeTemplateSiteidList = account?.modernHomeTemplateSiteidList;

  const isInModernOrgId = modernHomeTemplateOrgidList?.includes(organizationId?.toString() as string);
  const isInModernSiteId = modernHomeTemplateSiteidList?.includes(siteId?.toString() as string);

  const useModernTemplate = account?.isRSITemplate && (
    (isInModernOrgId && isInModernSiteId) || 
    (account?.isModernTemplate)
  );

  // Persist sidebar state in localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('modern-sidebar-collapsed');
    if (savedState !== null) {
      setSidebarCollapsed(savedState === 'true');
    }
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.modern-sidebar') && !target.closest('.modern-sidebar-global__mobile-toggle')) {
          setMobileMenuOpen(false);
        }
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleToggleCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('modern-sidebar-collapsed', String(newState));
  };

  const handleMobileMenuToggle = () => {
    const newMobileState = !mobileMenuOpen;
    setMobileMenuOpen(newMobileState);
    // When opening mobile menu, ensure sidebar is expanded (not collapsed)
    if (newMobileState && sidebarCollapsed) {
      setSidebarCollapsed(false);
    }
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  if (!useModernTemplate) {
    return <>{children}</>;
  }

  const accountName = account?.name || '';
  const userName = items?.firstName && items?.lastName 
    ? `${items.firstName} ${items.lastName[0]}` 
    : user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName[0]}`
    : 'User';
  const memberId = String(items?.rsiId || user?.keyid || '');
  // Get points from profile if available (only show if points is a valid number)
  const points = profile && profile !== null && typeof profile.points === 'number' ? profile.points : undefined;

  // Get logo from menu items or account preferences
  const logoDB = getPreferenceFileUrlFromAccount(account as IAccount, LOGO_FIELD_KEY);
  const logoUrl = !isEmpty(items?.logoUrl) ? items?.logoUrl : !isEmpty(logoDB) ? logoDB : null;

  return (
    <div className="modern-sidebar-global">
      {/* Mobile Header */}
      <header className="modern-sidebar-global__mobile-header">
        <button
          className="modern-sidebar-global__mobile-toggle"
          onClick={handleMobileMenuToggle}
          aria-label="Toggle menu"
        >
          <svg
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {mobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
        {logoUrl ? (
          <Link to={`/${accountName}${Routes.Home}`} className="modern-sidebar-global__mobile-logo">
            <img src={logoUrl} alt="Logo" />
          </Link>
        ) : (
          <Link to={`/${accountName}${Routes.Home}`} className="modern-sidebar-global__mobile-logo-text">
            TravelCore
          </Link>
        )}
      </header>
      {mobileMenuOpen && (
        <div
          className="modern-sidebar-global__overlay"
          onClick={handleMobileMenuClose}
        />
      )}
      <ModernSidebar
        collapsed={mobileMenuOpen ? false : sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
        accountName={accountName}
        userName={userName}
        memberId={memberId}
        points={points}
        logoUrl={logoUrl || undefined}
        account={account}
        items={items || undefined}
        mobileOpen={mobileMenuOpen}
        onMobileClose={handleMobileMenuClose}
      />
      <main className={`modern-sidebar-global__content ${sidebarCollapsed ? 'modern-sidebar-global__content--collapsed' : ''}`}>
        {children}
      </main>
    </div>
  );
}

// Export useModernTemplate check for use in other components
export function useModernTemplateCheck(account: any, items: any): boolean {
  const siteId = items?.siteId;
  const organizationId = items?.organizationId;
  const modernHomeTemplateOrgidList = account?.modernHomeTemplateOrgidList;
  const modernHomeTemplateSiteidList = account?.modernHomeTemplateSiteidList;

  const isInModernOrgId = modernHomeTemplateOrgidList?.includes(organizationId?.toString() as string);
  const isInModernSiteId = modernHomeTemplateSiteidList?.includes(siteId?.toString() as string);

  return account?.isRSITemplate && (
    (isInModernOrgId && isInModernSiteId) || 
    (account?.isModernTemplate)
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore,
    menuStore: state.navigationMenuStore,
    myProfileStore: state.myProfileStore,
  };
};

export const ModernSidebarGlobal = connect(mapStateToProps)(ModernSidebarGlobalComponent);

